import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cva } from "class-variance-authority";
import { Check } from "lucide-react";
import { cn } from "../../lib/utils";
import { Loader } from "./loader";

const checkboxButtonVariants = cva(
  "inline-flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer focus-within:outline-none justify-start max-w-[260px]",
  {
    variants: {
      variant: {
        default: "",
        primary: "",
        secondary: "",
      },
      size: {
        sm: "px-3 py-2 text-sm max-w-[220px] gap-2",
        default: "px-4 py-3 text-base max-w-[260px] gap-2.5",
        lg: "px-5 py-4 text-lg max-w-[300px] gap-3",
      },
      state: {
        default:
          "border border-[#D9D9D9] bg-white text-gray-700 hover:bg-gray-50",
        checked: "border-2 border-primary-3 bg-primary-7 text-gray-900",
        disabled:
          "opacity-50 cursor-not-allowed hover:bg-white border border-[#D9D9D9] bg-white",
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        state: "checked",
        class: "border-2 border-primary-3 bg-primary-7 text-gray-900",
      },
      {
        variant: "secondary",
        state: "checked",
        class: "border-2 border-primary-3 bg-primary-7 text-gray-900",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      state: "default",
    },
  }
);

const CheckboxButton = React.forwardRef(
  (
    {
      className,
      variant = "default",
      size = "default",
      checked = false,
      disabled = false,
      loading = false,
      children,
      label,
      onCheckedChange,
      hideCheckbox = false,
      checkboxPosition = "left",
      ...props
    },
    ref
  ) => {
    const [isChecked, setIsChecked] = React.useState(checked);

    React.useEffect(() => {
      setIsChecked(checked);
    }, [checked]);

    const handleCheckedChange = (newChecked) => {
      setIsChecked(newChecked);
      onCheckedChange?.(newChecked);
    };

    const state = disabled ? "disabled" : isChecked ? "checked" : "default";

    const checkboxElement = !hideCheckbox && (
      loading ? (
        <div className="h-5 w-5 flex items-center justify-center flex-shrink-0">
          <Loader
            variant="spinner"
            size="sm"
            color="primary"
          />
        </div>
      ) : (
        <CheckboxPrimitive.Root
          ref={ref}
          className={cn(
            "h-5 w-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0",
            isChecked
              ? "bg-secondary-2 border-secondary-2"
              : "border-gray-300 bg-white",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          checked={isChecked}
          disabled={disabled}
          onCheckedChange={handleCheckedChange}
          {...props}
        >
          <CheckboxPrimitive.Indicator className="flex items-center justify-center">
            <Check className="h-3 w-3 text-white font-bold stroke-[3]" />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
      )
    );

    const content = (
      <>
        {checkboxPosition === "left" && checkboxElement}
        <span className="flex-1 truncate capitalize" title={label || children}>
          {label || children}
        </span>
        {checkboxPosition === "right" && checkboxElement}
      </>
    );

    return (
      <label
        className={cn(
          checkboxButtonVariants({ variant, size, state }),
          disabled && "cursor-not-allowed",
          className
        )}
      >
        {hideCheckbox ? (
          <CheckboxPrimitive.Root
            ref={ref}
            className="sr-only"
            checked={isChecked}
            disabled={disabled}
            onCheckedChange={handleCheckedChange}
            {...props}
          >
            <CheckboxPrimitive.Indicator />
          </CheckboxPrimitive.Root>
        ) : null}
        {content}
      </label>
    );
  }
);

CheckboxButton.displayName = "CheckboxButton";

// Group component for multiple checkbox buttons
const CheckboxButtonGroup = React.forwardRef(
  (
    {
      className,
      children,
      value = [],
      onValueChange,
      disabled = false,
      orientation = "horizontal",
      ...props
    },
    ref
  ) => {
    const handleItemChange = (itemValue, checked) => {
      if (disabled) return;

      let newValue;
      if (checked) {
        newValue = [...value, itemValue];
      } else {
        newValue = value.filter((v) => v !== itemValue);
      }
      onValueChange?.(newValue);
    };

    return (
      <div
        ref={ref}
        className={cn(
          "flex gap-2",
          orientation === "vertical" ? "flex-col" : "flex-row flex-wrap",
          className
        )}
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            const itemValue = child.props.value;
            const isChecked = value.includes(itemValue);

            return React.cloneElement(child, {
              checked: isChecked,
              disabled: disabled || child.props.disabled,
              loading: child.props.loading,
              onCheckedChange: (checked) => {
                handleItemChange(itemValue, checked);
                child.props.onCheckedChange?.(checked);
              },
            });
          }
          return child;
        })}
      </div>
    );
  }
);

CheckboxButtonGroup.displayName = "CheckboxButtonGroup";

export { CheckboxButton, CheckboxButtonGroup };
